import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  MessageSquare,
  Trash2,
  Send,
  Star,
  Split,
  LogOut,
  PanelLeft,
  Square,
  Sun,
  Moon,
  Bot,
  AlertCircle,
  Loader,
  Heart,
  Ellipsis,
  Pencil,
  SquarePause,
  BookCheck,
  Info,
  User
} from 'lucide-react';
import { Toaster } from 'sonner';
import { useTheme } from './ThemeContext.tsx';
import logo from './assets/Logo_grippy_opt.png';
import Guideline from './components/Guideline/page.tsx';
// import ReleaseNote from './components/ReleaseNote/page.tsx';
import Sidebar from './components/sidebar/Sidebar.tsx'
import ChatPanel from './components/chat/ChatPanel.tsx';
import { Session, Message } from './types/chat.ts'
import { QueryResponse, apiService, GetSessionResponse, ListSessionsResponse } from './services/api.ts';
import { sanitizeMarkdown, buildDataFromHistoryInteraction, mergeSessionsKeepExisting, genLocalId, genSessionId, rowsToTableData } from './utils/chatUtils.ts';
import releaseNotesData from './data/release-notes.json';

type AppProps = { onLogout?: () => void; };


const App: React.FC<AppProps> = ({ onLogout }) => {
  const { theme, toggleTheme, colors, isMobile } = useTheme();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);

  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isGuidelineOpen, setIsGuidelineOpen] = useState<boolean>(false);
  const [hasUnReadNotes, setHasUnReadNotes] = useState<boolean>(false);
  const [currentRequestAbortController, setCurrentRequestAbortController] = useState<AbortController | null>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const prevSessionIdRef = useRef<string | null>(null);
  const prevMessageCountRef = useRef<number>(0);
  const initialChatCreatedRef = useRef<boolean>(false);

  const activeSession = sessions.find(s => s.id === activeSessionId);
  const hasStartedChat = activeSession ? activeSession.messages.length > 0 : false;

  useEffect(() => {
    const back = sessionStorage.getItem('postLoginRedirect');
    if (back) {
      sessionStorage.removeItem('postLoginRedirect');
      const current = window.location.pathname + window.location.search + window.location.hash;
      if (current !== back) {
        window.history.replaceState(null, '', back);
      }
    }
  }, []);

  useEffect(() => {
    if (sessions.length === 0 && !initialChatCreatedRef.current) {
      initialChatCreatedRef.current = true;
      handleNewChat();
    }
  }, [sessions.length]);

  useEffect(() => {
    const lastSeenVersion = localStorage.getItem('lastSeenReleaseNote');
    const currentVersion = releaseNotesData[0]?.version || '';
    if (lastSeenVersion !== currentVersion) {
      setHasUnReadNotes(true);
    }
  }, []);

  // Load sessions from backend on first mount
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res: ListSessionsResponse = await apiService.listSessions();
        if (res?.success && Array.isArray(res.sessions)) {
          const remote: Session[] = res.sessions.map(s => ({
            id: s.session_id,
            title: s.title || 'Untitled',
            isFavorite: !!s.is_favorite,
            messages: [],
            loaded: false,
          }));
          if (!cancelled) {
            setSessions(prev => mergeSessionsKeepExisting(prev, remote));
            // IMPORTANT:
            // - Do NOT call setActiveSessionId(...) here
            // - Do NOT call apiService.setSessionId(...) here
            // This prevents the main chat from switching/refreshing.
          }
        }
      } catch {
        // Swallow errors; keep whatever we already show
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Auto-scroll chat view when messages change
  useEffect(() => {
    const currentSessionId = activeSession?.id ?? null;
    const currentMessageCount = activeSession?.messages?.length ?? 0;
    const sessionChanged = prevSessionIdRef.current !== currentSessionId;
    const hasNewMessage = currentMessageCount > prevMessageCountRef.current;

    if (chatAreaRef.current && (sessionChanged || hasNewMessage)) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }

    prevSessionIdRef.current = currentSessionId;
    prevMessageCountRef.current = currentMessageCount;
  }, [activeSession?.id, activeSession?.messages]);

  // --- Actions ---
  const handleNewChat = () => {
    // Check if the current active session is already a fresh new chat
    const currentActive = sessions.find(s => s.id === activeSessionId);
    if (currentActive && currentActive.messages.length === 0 && currentActive.title === "New Chat") {
      // Already on a new chat, just clear input and return
      setUserInput('');
      return;
    }

    // Check if there is ANY existing empty new chat in the list
    const existingEmpty = sessions.find(s => s.messages.length === 0 && s.title === "New Chat");
    if (existingEmpty) {
      setActiveSessionId(existingEmpty.id);
      apiService.setSessionId(existingEmpty.id);
      setUserInput('');
      return;
    }

    const newSessionId = genSessionId();
    const newSession: Session = { id: newSessionId, title: "New Chat", messages: [], loaded: true };
    setSessions(prev => [newSession, ...prev]);
    setActiveSessionId(newSessionId);
    apiService.setSessionId(newSessionId);
    setUserInput('');
  };

  const handleSelectSession = async (sessionId: string) => {
    setActiveSessionId(sessionId);

    setSessions(prev => {
      const s = prev.find(x => x.id === sessionId);
      // If we already loaded it before, just switch
      if (s?.loaded) return prev;
      return prev;
    });

    // Load from backend only if not loaded yet
    const target = sessions.find(s => s.id === sessionId);
    if (!target || target.loaded) return;

    try {
      const sessionData = await apiService.getSession(sessionId);

      // Check if we have valid session data and interactions
      if (!sessionData || !sessionData.success || !Array.isArray(sessionData.interactions)) {
        console.warn('Invalid session data for session:', sessionId, 'Response:', sessionData);
        return;
      }

      // Convert each interaction to: user message + bot message
      const built = sessionData.interactions.flatMap((it) => {
        const userMsg: Message = {
          id: `u_${it.interaction_id}`,
          sender: 'user',
          text: it.user_question,
        };
        const botMsg: Message = {
          id: `b_${it.interaction_id}`,
          sender: 'bot',
          text: it.grippy_answer ?? '',
          data: buildDataFromHistoryInteraction(
            it.dataframe_json ?? undefined,
            it.figure_json ?? undefined,
            it.interaction_id
          ),
          interactionId: it.interaction_id,
        };
        return [userMsg, botMsg];
      });

      setSessions(prev =>
        prev.map(s => (s.id === sessionId
          ? { ...s, messages: built, loaded: true }
          : s
        ))
      );

      // also point apiService to this session so future /api/query uses it
      apiService.setSessionId(sessionId);
    } catch (e) {
      // failed to load history → keep the session selectable but empty
      console.error('Failed to load interactions', e);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    // Optimistic UI
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    if (activeSessionId === sessionId) {
      const next = sessions.find(s => s.id !== sessionId);
      if (next) {
        setActiveSessionId(next.id);
        apiService.setSessionId(next.id);
      } else {
        handleNewChat();
      }
    }
    // Best effort server delete
    try { await apiService.deleteSession(sessionId); } catch (e) { console.warn('Delete failed:', e); }
  };

  const handleSendMessage = async (text: string) => {
    if (!text.trim() || !activeSessionId || isProcessing) return;

    // Create abort controller for this request
    const abortController = new AbortController();
    setCurrentRequestAbortController(abortController);
    setIsProcessing(true);

    const userMessage: Message = { id: genLocalId(), text, sender: 'user' };
    const loadingMessage: Message = {
      id: genLocalId(),
      text: 'Processing your query...',
      sender: 'bot',
      isLoading: true,
      timestamp: Date.now()
    };

    // Add user message and loading message immediately
    setSessions(prev => prev.map(s => {
      if (s.id === activeSessionId) {
        const updated: Session = { ...s, messages: [...s.messages, userMessage, loadingMessage] };
        if (s.messages.length === 0) {
          updated.title = text.substring(0, 25) + (text.length > 25 ? '...' : '');
        }
        return updated;
      }
      return s;
    }));

    setUserInput('');

    try {
      const response = await apiService.sendQuery(text, abortController.signal);

      // Replace loading message with actual response
      setSessions(prev => prev.map(s => {
        if (s.id === activeSessionId) {
          const updatedMessages = s.messages.map(msg => {
            if (msg.id === loadingMessage.id) {
              if (response.success) {
                // If this was the first user turn for this session, ensure sidebar title reflects it
                const userTurns = s.messages.filter(m => m.sender === 'user').length;
                if (userTurns === 1) {
                  // already set above, but keep consistent if needed
                }

                const realId =
                  (response as any)?.interaction_id ??
                  (response as any)?.interactionId ??
                  undefined;
                return {
                  id: realId ? `b_${realId}` : msg.id,
                  text: response.response || 'No response received',
                  sender: 'bot' as const,
                  data: response.data,
                  isLoading: false,
                  interactionId: realId,
                };
              } else {
                return {
                  id: msg.id,
                  text: 'Sorry, I encountered an error processing your request.',
                  sender: 'bot' as const,
                  error: response.error,
                  isLoading: false
                };
              }
            }
            return msg;
          });
          return { ...s, messages: updatedMessages };
        }
        return s;
      }));
    } catch (error) {
      // Check if the error is due to abort
      if (error instanceof Error && error.name === 'AbortError') {
        // Replace loading message with user-stopped message
        setSessions(prev => prev.map(s => {
          if (s.id === activeSessionId) {
            const updatedMessages = s.messages.map(msg => {
              if (msg.id === loadingMessage.id) {
                return {
                  id: msg.id,
                  text: 'Query stopped by user.',
                  sender: 'bot' as const,
                  isLoading: false
                };
              }
              return msg;
            });
            return { ...s, messages: updatedMessages };
          }
          return s;
        }));
      } else {
        setSessions(prev => prev.map(s => {
          if (s.id === activeSessionId) {
            const updatedMessages = s.messages.map(msg => {
              if (msg.id === loadingMessage.id) {
                return {
                  id: msg.id,
                  text: 'Sorry, I could not connect to the server. Please try again.',
                  sender: 'bot' as const,
                  error: error instanceof Error ? error.message : 'Unknown error',
                  isLoading: false
                };
              }
              return msg;
            });
            return { ...s, messages: updatedMessages };
          }
          return s;
        }));
      }
    } finally {
      setIsProcessing(false);
      setCurrentRequestAbortController(null);
    }
  };

  const handleStopProcessing = () => {
    if (currentRequestAbortController) {
      currentRequestAbortController.abort();
      setCurrentRequestAbortController(null);
      setIsProcessing(false);
    }
  };

  const handleMessageUpdate = (messageId: string, updates: Partial<Message>) => {
    setSessions(prev => prev.map(session => {
      if (session.id === activeSessionId) {
        return {
          ...session,
          messages: session.messages.map(msg =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          )
        };
      }
      return session;
    }));
  };

  const handleRenameSession = async (sessionId: string, newTitle: string) => {
    if (!newTitle.trim()) return;

    // Optimistic UI update
    setSessions(prev => prev.map(s =>
      s.id === sessionId ? { ...s, title: newTitle.trim() } : s
    ));

    // Update on backend
    try {
      await apiService.renameSession(sessionId, newTitle.trim());
    } catch (error) {
      console.error('Failed to rename session:', error);
      // Revert optimistic update on error
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, title: sessions.find(ss => ss.id === sessionId)?.title || 'Untitled' } : s
      ));
    }
  };

  return (
    <>
      <div className={`flex h-screen w-screen font-sans ${colors.bg.primary} ${colors.text.primary}`}>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            sessions={sessions}
            activeSessionId={activeSessionId}
            onNewChat={handleNewChat}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteSession}
            onRenameSession={handleRenameSession}
            onToggleFavorite={async (id, isFav) => {
              setSessions(prev => prev.map(s => s.id === id ? { ...s, isFavorite: isFav } : s));
              try { await apiService.favoriteSession(id, isFav); } catch (e) { /* ignore */ }
            }}
            onLogout={onLogout}
            isMobile={false}
          />
        )}
        {/* Mobile Sidebar */}
        {isMobile && (
          <Sidebar
            isCollapsed={false}
            onToggle={() => { }}
            sessions={sessions}
            activeSessionId={activeSessionId}
            onNewChat={handleNewChat}
            onSelectSession={handleSelectSession}
            onDeleteSession={handleDeleteSession}
            onRenameSession={handleRenameSession}
            onToggleFavorite={async (id, isFav) => {
              setSessions(prev => prev.map(s => s.id === id ? { ...s, isFavorite: isFav } : s));
              try { await apiService.favoriteSession(id, isFav); } catch (e) { /* ignore */ }
            }}
            onLogout={onLogout}
            isMobile={true}
            isMobileOpen={isMobileSidebarOpen}
            onMobileClose={() => setIsMobileSidebarOpen(false)}
          />
        )}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className={`flex items-center justify-between p-[8.5px] ${colors.bg.secondary} border-b ${colors.border.primary} z-10`}>
            <div className="flex items-center gap-2">
              {isMobile && (
                <button
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className={`p-2 rounded-md ${colors.interactive.secondaryHover} ${colors.text.secondary}`}
                  title="Open sidebar"
                >
                  <ChevronRight size={20} />
                </button>
              )}
              <img src={logo} alt="Grippy Logo" className="h-10 scale-150" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsGuidelineOpen(!isGuidelineOpen)}
                title="Guidelines"
                className={`relative p-2 rounded-md transition-colors bg-[#13670B] text-white ${colors.interactive.primaryHover}`}
              >
                <BookCheck size={18} />
                {hasUnReadNotes && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white"></span>
                  </span>
                )}
              </button>
              {/* <ReleaseNote /> */}
            </div>
          </header>
          <main className="flex-1 flex flex-row">
            {/* Chat Layout - simplified to only show chat panel */}
            <div className="flex-1 flex flex-col h-full">
              <ChatPanel
                chatAreaRef={chatAreaRef}
                hasStartedChat={hasStartedChat}
                activeSession={activeSession}
                userInput={userInput}
                setUserInput={setUserInput}
                onSendMessage={handleSendMessage}
                onMessageUpdate={handleMessageUpdate}
                isProcessing={isProcessing}
                onStopProcessing={handleStopProcessing}
                sessionId={activeSessionId}
                onChartRequest={handleSendMessage}
              />
            </div>
          </main>
        </div>
      </div>

      {/* Guideline Component */}
      <Guideline
        isOpen={isGuidelineOpen}
        onClose={() => setIsGuidelineOpen(false)}
        hasUnRead={hasUnReadNotes}
        onMarkRead={() => {
          setHasUnReadNotes(false);
          localStorage.setItem('lastSeenReleaseNote', releaseNotesData[0]?.version || '');
        }}
      />

      {/* Sonner Toaster for notifications */}
      <Toaster
        position="bottom-right"
        expand={false}
        richColors
        closeButton
        toastOptions={{
          duration: 6000,
          style: {
            maxWidth: '400px',
          },
        }}
      />
    </>
  );
};




export default App;
